"use strict";
/**
 * Per-form item label overrides — types and helpers shared by form renderers
 * (hds-forms-js) and patient/diary apps (hds-webapp, doctor-dashboard).
 *
 * Storage model: `CollectorRequest.sections[].itemCustomizations[itemKey].labels`
 * — see ItemCustomization below. The same itemKey may be requested by multiple
 * forms (across multiple contacts) with different labels. Renderers should
 * surface every variant with attribution rather than picking one.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSectionItemLabels = getSectionItemLabels;
exports.collectItemLabelsFromSections = collectItemLabelsFromSections;
exports.collectItemLabels = collectItemLabels;
/** Read labels (if any) for itemKey from a single section. */
function getSectionItemLabels(section, itemKey) {
    if (!section.itemKeys.includes(itemKey))
        return undefined;
    const cust = section.itemCustomizations?.[itemKey];
    return cust?.labels;
}
function isEmptyLabels(l) {
    return !l || (l.question == null && l.description == null && (l.options == null || Object.keys(l.options).length === 0));
}
/**
 * Collect label overrides for itemKey from a list of (section, source) pairs.
 * One entry per section that uses the item; deduplicated by label content.
 */
function collectItemLabelsFromSections(itemKey, sources, opts = {}) {
    const requireLabels = opts.requireLabels ?? true;
    const deduplicate = opts.deduplicate ?? true;
    const out = [];
    const seen = new Set();
    for (const { section, source } of sources) {
        if (!section.itemKeys.includes(itemKey))
            continue;
        const labels = getSectionItemLabels(section, itemKey) || {};
        if (requireLabels && isEmptyLabels(labels))
            continue;
        const entry = { ...labels, source };
        if (deduplicate) {
            const sig = JSON.stringify({ q: entry.question, d: entry.description, o: entry.options });
            if (seen.has(sig))
                continue;
            seen.add(sig);
        }
        out.push(entry);
    }
    return out;
}
/**
 * Gather labels for itemKey from every CMC relationship on the given contacts.
 * Each entry is attributed to the contact and form (FormSpec) it came from.
 *
 * Post-plan-61 Phase C: walks `contact.cmcRelationships[].hdsFormSpec.sections`
 * (no more legacy CollectorClient). Relationships without a mirrored FormSpec
 * snapshot are skipped.
 */
function collectItemLabels(itemKey, contacts, opts = {}) {
    const sources = [];
    for (const contact of contacts) {
        for (const rel of contact.cmcRelationships) {
            const spec = rel.hdsFormSpec;
            if (!spec || !Array.isArray(spec.sections))
                continue;
            const formTitle = spec.title;
            for (const sec of spec.sections) {
                // FormSpec sections (AppTemplateSection) are structurally compatible
                // with CollectorSectionInterface for label-extraction purposes.
                const section = sec;
                sources.push({
                    section,
                    source: {
                        contactName: contact.displayName,
                        formTitle,
                        sectionKey: section.key,
                        requestKey: rel.accessId
                    }
                });
            }
        }
    }
    return collectItemLabelsFromSections(itemKey, sources, opts);
}
